import * as React from "react";
import { NativeTileShowcaseRegionProps, NativeTileShowcaseRegionFlexModuleModelResult } from "./typings";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "@shared-ui/viewport-context";
import { BlossomComponents } from "components/flexFramework/BlossomComponents/BlossomComponents";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { UitkSpacing, UitkSpacingProps } from "uitk-react-spacing";
import { observer } from "mobx-react";
import { withStores } from "stores";
import { SpacingSize } from "src/components/utility/FlexAttributesUtil";
import { hasRenderableChildren } from "src/stores/ExperienceTemplateStore/shouldRender";
import { ExperienceTemplateRenderer } from "experience-template-renderer-react";

export const NativeTileShowcaseRegion = withStores(
  "flexModuleModelStore",
  "context"
)(
  observer((props: NativeTileShowcaseRegionProps) => {
    const { context, blossomComponent, flexModuleModelStore, templateComponent } = props;

    if (!templateComponent) {
      return null;
    }

    const {
      metadata: { id },
    } = templateComponent;

    const model = (flexModuleModelStore.getModel(id) as NativeTileShowcaseRegionFlexModuleModelResult) || null;

    if (!model) {
      return null;
    }

    const { mobileWidth, title, innerTitle, tabletWidth, desktopWidth, padding } = model;

    if (!hasRenderableChildren(templateComponent, flexModuleModelStore)) {
      return null;
    }

    const headingClass = "uitk-type-display-700";

    const titleContent = (
      <>
        {title && (
          <UitkSpacing padding={{ blockend: "unset", inlinestart: "unset" }}>
            <h2 className={headingClass}>{title}</h2>
          </UitkSpacing>
        )}
        {innerTitle && (
          <UitkSpacing padding={{ blockend: "unset", inlinestart: "unset" }}>
            <h2 className={headingClass}>{innerTitle}</h2>
          </UitkSpacing>
        )}
      </>
    );

    const hasNoPadding = padding === SpacingSize.NONE;
    const marginSpacing: UitkSpacingProps["margin"] = hasNoPadding
      ? { blockstart: "unset", blockend: "unset" }
      : { blockstart: "six", blockend: "three" };

    const blossomComponentChildren = blossomComponent && (
      <BlossomComponents blossomComponent={blossomComponent} context={context} />
    );
    const templateComponentChildren = !blossomComponent && templateComponent.children && (
      <ExperienceTemplateRenderer templateComponents={templateComponent.children} />
    );

    const flexContentDesktop = (
      <UitkSpacing margin={marginSpacing}>
        <div>
          {titleContent}
          <UitkLayoutFlex direction="row" wrap="nowrap">
            {blossomComponentChildren}
            {templateComponentChildren}
          </UitkLayoutFlex>
        </div>
      </UitkSpacing>
    );

    const flexContentNonDesktop = (
      <UitkSpacing margin={marginSpacing}>
        <div>
          {titleContent}
          <UitkLayoutFlex direction="column" space="six" wrap="wrap">
            {blossomComponentChildren}
            {templateComponentChildren}
          </UitkLayoutFlex>
        </div>
      </UitkSpacing>
    );

    return (
      <Viewport>
        {mobileWidth === "hidden" ? <ViewSmall /> : <ViewSmall>{flexContentNonDesktop}</ViewSmall>}

        {tabletWidth === "hidden" ? <ViewMedium /> : <ViewMedium>{flexContentNonDesktop}</ViewMedium>}

        {desktopWidth === "hidden" ? <ViewLarge /> : <ViewLarge>{flexContentDesktop}</ViewLarge>}
      </Viewport>
    );
  })
);

export default NativeTileShowcaseRegion;
